import { Button, TextInput } from '@mantine/core';
import { IconAdjustmentsHorizontal, IconSearch } from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusWithin, useDebouncedValue } from '@mantine/hooks';

interface SearchInputProps {
  onSearch: (searchText: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xs' | 'xl';
  onClickFilters?: () => void;
  initialValue?: string;
  autoFocus?: boolean;
  debounce?: number;
}

export default function SearchInput({
  onSearch,
  placeholder,
  className,
  size = 'sm',
  onClickFilters,
  initialValue = '',
  autoFocus = false,
  debounce = 300
}: SearchInputProps) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(initialValue);
  const { ref, focused } = useFocusWithin();

  useEffect(() => {
    setSearchText(initialValue);
  }, [initialValue]);

  const [debouncedSearchText] = useDebouncedValue(searchText, debounce);

  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onSearchRef.current(debouncedSearchText);
  }, [debouncedSearchText]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
  };

  return (
    <div className="flex gap-1 items-center w-full">
      {onClickFilters && (
        <Button
          size="md"
          radius="xl"
          variant="subtle"
          color="black"
          leftSection={<IconAdjustmentsHorizontal size={16} />}
          onClick={onClickFilters}
        >
          {t('filters')}
        </Button>
      )}
      <div
        ref={ref}
        className={`
          ${className} bg-white border rounded-full flex-1 transition-all duration-150
          ${focused ? 'border-blue-600 shadow-sm ring-1 ring-blue-600/10' : 'border-gray-300'}
        `}
      >
        <TextInput
          autoFocus={autoFocus}
          variant='subtle'
          placeholder={placeholder || t('search') + '...'}
          value={searchText}
          onChange={handleChange}
          size={size}
          leftSection={<IconSearch size={18} stroke={2} color="#555" />}
        />
      </div>
    </div>
  );
}

